/**
 * PdwTabStrip — PDW Step 9 canonical 9-tab navigation (SPEC Screen 17, line 1546).
 *
 * 9 tabs in the canonical order:
 *   Review · CIP · Origin · Profile · Contact · SSN-TIN · Work-School ·
 *   Verify · Documents
 *
 * Each tab has a status pill:
 *   - ✓ done — all required fields completed
 *   - ○ unvisited — never opened (default)
 *   - ⚠ warnings — some fields invalid or missing (count surfaced)
 *
 * Free navigation per Bill 2026-05-11 — FSR can click any tab any time.
 * No gating between tabs. The Add Person to Cart button on the Review
 * footer is what enforces required-field completeness (soft warning on
 * unvisited tabs; HARD-STOP on expired ID etc).
 *
 * Bill 2026-05-13 — second sub-batch of PDW Step 9 rollout (after
 * v0.14.37 PdwStatusBadges). The section-per-tab segmentation lands in
 * v0.14.39; for this batch the strip is presentation-only.
 */

export type PdwTabKey =
  | 'review'
  | 'citizenship'
  | 'cip'
  | 'origin'
  | 'profile'
  | 'ownership'
  | 'contact'
  | 'ssn-tin'
  | 'work-school'
  | 'verify'
  | 'documents';

export type PdwTabStatus = 'unvisited' | 'visited' | 'done' | 'warn';

export interface PdwTabState {
  status: PdwTabStatus;
  /** Optional count surfaced next to ⚠ icon (e.g. number of warnings). */
  warnCount?: number;
  /**
   * Hide-until-selected (Bill 2026-06-12) — fresh-add Individual renders only
   * the Citizenship block until a citizenship tile is picked; the pills for
   * the hidden sections render disabled + dimmed (no section to jump to yet).
   */
  disabled?: boolean;
}

export interface PdwTabStripProps {
  activeTab: PdwTabKey;
  onTabChange: (tab: PdwTabKey) => void;
  /** Per-tab state for the status pill. Defaults to unvisited for missing keys. */
  tabState?: Partial<Record<PdwTabKey, PdwTabState>>;
  /**
   * Left-to-right tab order. Mode-dependent (D2): existing-CIF / review-add
   * lands Review FIRST (canonical), Add-new pushes Review LAST (far right).
   * Defaults to {@link PDW_TAB_ORDER}. Only keys present here render, so a
   * caller may also pass an entity-appropriate subset.
   */
  tabOrder?: ReadonlyArray<PdwTabKey>;
}

interface TabSpec {
  readonly key: PdwTabKey;
  readonly label: string;
}

export const PDW_TAB_ORDER: ReadonlyArray<PdwTabKey> = [
  'review',
  'cip',
  'origin',
  'profile',
  'ownership',
  'contact',
  'ssn-tin',
  'work-school',
  'verify',
  'documents',
];

const TABS: ReadonlyArray<TabSpec> = [
  { key: 'review',      label: 'Review' },
  // Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6) — the
  // consolidated Individual body leads with the Citizenship block; this pill
  // jump-scrolls to it. 'origin' stays in the union for legacy/entity callers.
  { key: 'citizenship', label: 'Citizenship' },
  { key: 'cip',         label: 'CIP' },
  { key: 'origin',      label: 'Origin' },
  { key: 'profile',     label: 'Profile' },
  { key: 'ownership',   label: 'Ownership' },
  { key: 'contact',     label: 'Contact' },
  { key: 'ssn-tin',     label: 'SSN-TIN' },
  { key: 'work-school', label: 'Work-School' },
  { key: 'verify',      label: 'Verify' },
  { key: 'documents',   label: 'Documents' },
];

const STATUS_ICON: Record<PdwTabStatus, string> = {
  'unvisited': '○',
  'visited':   '·',
  'done':      '✓',
  'warn':      '⚠',
};

const TAB_LABEL: Record<PdwTabKey, string> = TABS.reduce(
  (acc, t) => {
    acc[t.key] = t.label;
    return acc;
  },
  {} as Record<PdwTabKey, string>,
);

export function PdwTabStrip({
  activeTab,
  onTabChange,
  tabState = {},
  tabOrder = PDW_TAB_ORDER,
}: PdwTabStripProps): JSX.Element {
  const ordered: ReadonlyArray<TabSpec> = tabOrder.map((key) => ({ key, label: TAB_LABEL[key] }));
  return (
    <div className="pdw-tab-strip" role="tablist" aria-label="Person review tabs">
      {ordered.map((t) => {
        const isActive = t.key === activeTab;
        const state = tabState[t.key] ?? { status: 'unvisited' };
        const disabled = !!state.disabled;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive ? 'true' : 'false'}
            disabled={disabled}
            onClick={() => { if (!disabled) onTabChange(t.key); }}
            className={`pdw-tab pdw-tab--${state.status} ${isActive ? 'pdw-tab--active' : ''}`.trim()}
            style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            title={disabled ? 'Select the citizenship status first' : undefined}
          >
            <span className={`pdw-tab__status pdw-tab__status--${state.status}`} aria-hidden>
              {STATUS_ICON[state.status]}
            </span>
            <span className="pdw-tab__label">{t.label}</span>
            {state.status === 'warn' && state.warnCount !== undefined && state.warnCount > 0 && (
              <span className="pdw-tab__warn-count">{state.warnCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
